/**
 * Order Management — reusable UI pieces (badges, clamped text, items table, countdown).
 */

import React, { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ShoppingBagOutlinedIcon from '@mui/icons-material/ShoppingBagOutlined';

import { COLORS, TERMINAL_STATUSES } from './orderManagementConfig';
import {
  clamp2LineSx,
  orderListItemCellSx,
  orderListMoneyCellSx,
  orderListQtyCellSx,
  orderListTotalCellSx,
  TD_SX,
} from './orderManagementStyles';
import {
  calcRemainingTime,
  formatRemainingLabel,
  getItemLineTotal,
  getStatusStyle,
} from './orderManagementOrderHelpers';

export function StatusBadge({ status }) {
  const { bg, color } = getStatusStyle(status);
  const label = String(status || '—')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-block',
        bgcolor: bg,
        color,
        borderRadius: '20px',
        px: 1.25,
        py: 0.35,
        fontSize: 11.5,
        fontFamily: 'Inter Bold, sans-serif',
        fontWeight: 600,
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );
}

export function ClampedText({ text, sx = {}, capitalize = false, ...typographyProps }) {
  if (text == null || String(text).trim() === '') return null;
  const str = String(text);
  const {
    display: _display,
    WebkitLineClamp: _lineClamp,
    WebkitBoxOrient: _boxOrient,
    overflow: _overflow,
    ...safeSx
  } = sx;

  return (
    <Tooltip title={str} placement='top' arrow>
      <Box component='span' sx={{ display: 'block', width: '100%', minWidth: 0 }}>
        <Typography
          component='span'
          sx={{
            width: '100%',
            ...(capitalize && { textTransform: 'capitalize' }),
            ...safeSx,
            ...clamp2LineSx,
          }}
          {...typographyProps}
        >
          {str}
        </Typography>
      </Box>
    </Tooltip>
  );
}

const CONTACT_HEADER_ACTIONS = [
  { type: 'call', Icon: PhoneOutlinedIcon, label: 'Call' },
  { type: 'sms', Icon: SmsOutlinedIcon, label: 'SMS' },
  { type: 'push', Icon: NotificationsOutlinedIcon, label: 'Push notification' },
];

/** Shared call / SMS / push icon button styles from active page theme. */
export function getContactIconButtonSx(colors) {
  const c = colors ?? COLORS;
  const bg = c.orderBadgeBg ?? c.headerBg ?? '#F3F4F6';
  const hoverBg = c.inactiveTabBadgeBg ?? c.headerBg ?? bg;
  return {
    width: 28,
    height: 28,
    borderRadius: '8px',
    bgcolor: bg,
    color: c.ink,
    border: `1px solid ${c.border}`,
    transition: 'background-color 0.15s ease, color 0.15s ease',
    '&:hover': { bgcolor: hoverBg, color: c.ink },
    '&.Mui-disabled': { bgcolor: '#F3F4F6', color: '#D1D5DB', borderColor: c.border },
  };
}

/** Compact call / SMS / push icons for info card headers. */
export function ContactHeaderIconButtons({ who = 'contact', onAction, colors }) {
  const iconSx = getContactIconButtonSx(colors);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {CONTACT_HEADER_ACTIONS.map(({ type, Icon, label }) => (
        <Tooltip key={type} title={`${label} — ${who}`} placement='top' arrow>
          <IconButton
            size='small'
            aria-label={`${label} ${who}`}
            onClick={() => onAction?.(type)}
            sx={iconSx}
          >
            <Icon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      ))}
    </Box>
  );
}

export function RemainingTimeCell({ order }) {
  const [now, setNow] = useState(() => Date.now());

  const isTerminal = TERMINAL_STATUSES.has(String(order?.internal_order_status ?? ''));

  useEffect(() => {
    if (isTerminal) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [isTerminal, order?.delivery_date, order?.delivery_time_slot]);

  const rem = calcRemainingTime(order, now);

  if (!rem) {
    return (
      <TableCell sx={TD_SX}>
        <Typography sx={{ fontFamily: 'Inter Regular, sans-serif', fontSize: 12, color: '#D1D5DB' }}>
          —
        </Typography>
      </TableCell>
    );
  }

  const label = formatRemainingLabel(rem.totalSeconds);
  const urgent = rem.isCritical;
  const tooltipTitle = rem.isOverdue
    ? `${label} overdue — delivery window has passed`
    : `${label} left until end of delivery slot`;

  return (
    <TableCell sx={{ ...TD_SX, overflow: 'hidden', maxWidth: 0 }}>
      <Tooltip title={tooltipTitle} placement='top' arrow>
        <Box
          component='span'
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            maxWidth: '100%',
            bgcolor: urgent ? '#FEF2F2' : '#F0FDF4',
            border: `1px solid ${urgent ? '#FECACA' : '#BBF7D0'}`,
            borderRadius: '20px',
            px: 1,
            py: 0.35,
            fontSize: 11.5,
            fontFamily: 'Inter Bold, sans-serif',
            fontWeight: 700,
            color: urgent ? '#DC2626' : '#166534',
            cursor: 'default',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            fontVariantNumeric: 'tabular-nums',
            boxSizing: 'border-box',
          }}
        >
          {label}
        </Box>
      </Tooltip>
    </TableCell>
  );
}

export function ItemsSection({ items, label, chipBg, chipColor, isFreeSection = false }) {
  if (!items.length) return null;
  return (
    <>
      <TableRow>
        <TableCell colSpan={4} sx={{ py: 0.75, px: 2.5, border: 'none', bgcolor: '#FAFAFA' }}>
          <Box
            component='span'
            sx={{
              fontSize: 10,
              fontFamily: 'Inter Bold, sans-serif',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: chipColor,
              bgcolor: chipBg,
              px: 1,
              py: 0.4,
              borderRadius: '5px',
            }}
          >
            {label}
          </Box>
        </TableCell>
      </TableRow>

      {items.map((item, idx) => {
        const notLast = idx < items.length - 1;
        const bdr = notLast ? `1px solid ${COLORS.rowDivider}` : 'none';
        const priceLabel = isFreeSection || item.isFree ? 'Free' : item.price != null ? `$${Number(item.price).toFixed(2)}` : '—';
        const totalLabel = isFreeSection || item.isFree
          ? 'Free'
          : item.price != null || item.toppings?.length > 0
            ? `$${getItemLineTotal(item).toFixed(2)}`
            : '—';

        return (
          <TableRow key={idx} sx={{ bgcolor: isFreeSection || item.isFree ? 'transparent' : 'transparent', '&:hover': { bgcolor: isFreeSection || item.isFree ? '#F9FAFB' : '#F9FAFB' } }}>
            <TableCell sx={{ ...orderListItemCellSx, borderBottom: bdr }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0, width: '100%', overflow: 'hidden' }}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '10px',
                    bgcolor: '#F3F4F6',
                    border: `1px solid ${COLORS.border}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <ShoppingBagOutlinedIcon sx={{ fontSize: 14, color: '#9CA3AF' }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                  <ClampedText
                    text={item.name}
                    capitalize
                    sx={{
                      fontFamily: 'Inter Bold, sans-serif',
                      fontSize: 13,
                      fontWeight: 600,
                      color: '#111827',
                    }}
                  />
                  {item.notes && (
                    <ClampedText
                      text={item.notes}
                      capitalize
                      sx={{
                        fontFamily: 'Inter Regular, sans-serif',
                        fontSize: 11,
                        color: '#9CA3AF',
                        mt: 0.1,
                      }}
                    />
                  )}
                  {item.promotion && (
                    <Tooltip title={item.promotion} placement='top' arrow>
                      <Box
                        component='span'
                        sx={{
                          display: 'inline-block',
                          maxWidth: '100%',
                          mt: 0.5,
                          fontSize: 11,
                          fontFamily: 'Inter Bold, sans-serif',
                          fontWeight: 600,
                          color: '#166534',
                          bgcolor: '#F0FDF4',
                          border: '1px solid #BBF7D0',
                          borderRadius: '6px',
                          px: 0.85,
                          py: 0.25,
                          lineHeight: 1.3,
                          boxSizing: 'border-box',
                          ...clamp2LineSx,
                        }}
                      >
                        {item.promotion}
                      </Box>
                    </Tooltip>
                  )}
                  {item.toppings?.length > 0 &&
                    item.toppings.map((topping, ti) => (
                      <ClampedText
                        key={ti}
                        capitalize
                        text={`+ ${topping.name}${Number(topping.qty) > 1 ? ` ×${topping.qty}` : ''}${topping.price != null ? ` · $${Number(topping.price).toFixed(2)}` : ''}`}
                        sx={{
                          fontFamily: 'Inter Regular, sans-serif',
                          fontSize: 11,
                          color: '#6B7280',
                          mt: 0.35,
                          lineHeight: 1.35,
                        }}
                      />
                    ))}
                </Box>
              </Box>
            </TableCell>

            <TableCell sx={{ ...orderListQtyCellSx, borderBottom: bdr }}>
              <Tooltip title={String(item.qty)} placement='top' arrow>
                <Box component='span' sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {item.qty}
                </Box>
              </Tooltip>
            </TableCell>

            <TableCell sx={{ ...orderListMoneyCellSx, borderBottom: bdr }}>
              <ClampedText
                text={priceLabel}
                sx={{
                  fontFamily: 'Inter Regular, sans-serif',
                  fontSize: 13,
                  color: isFreeSection || item.isFree ? '#166534' : COLORS.ink,
                  textAlign: 'right',
                  fontWeight: isFreeSection || item.isFree ? 600 : 400,
                }}
              />
            </TableCell>

            <TableCell sx={{ ...orderListTotalCellSx, borderBottom: bdr }}>
              <ClampedText
                text={totalLabel}
                sx={{
                  fontFamily: 'Inter Bold, sans-serif',
                  fontSize: 13,
                  fontWeight: 600,
                  color: isFreeSection || item.isFree ? '#166534' : '#111827',
                  textAlign: 'right',
                }}
              />
            </TableCell>
          </TableRow>
        );
      })}
    </>
  );
}
